// src/components/admin/page-management/section-forms/FeaturesForm.js
import React, { useState } from 'react';
import ColorPicker from '../../shared/ColorPicker';
import { Plus,Type,Space,Play, Settings,List,Palette,Star,Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const FeaturesForm = ({ content, updateContent }) => {
  const [expandedFeatureIndex, setExpandedFeatureIndex] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Initialize features if not present
  const features = content?.features || [
    {
      icon: 'star',
      title: 'Premium Quality',
      description: 'All our products are crafted with premium materials for exceptional durability and presentation.'
    },
    {
      icon: 'leaf',
      title: 'Eco-Friendly',
      description: 'We prioritize sustainable materials and manufacturing processes to minimize environmental impact.'
    },
    {
      icon: 'award',
      title: 'Custom Designs',
      description: 'Our design team works with you to create unique packaging that perfectly represents your brand.'
    },
    {
      icon: 'truck',
      title: 'Fast Delivery',
      description: 'With our efficient production and shipping, your orders will arrive right when you need them.'
    }
  ];

  // Add a new feature
  const addFeature = () => {
    const newFeatures = [...features, {
      icon: 'plus',
      title: 'New Feature',
      description: 'Description for the new feature'
    }];

    updateContent('features', newFeatures);
    setExpandedFeatureIndex(newFeatures.length - 1);
  };

  // Remove a feature
  const removeFeature = (index) => {
    const newFeatures = [...features];
    newFeatures.splice(index, 1);
    updateContent('features', newFeatures);

    if (expandedFeatureIndex === index) {
      setExpandedFeatureIndex(null);
    } else if (expandedFeatureIndex > index) {
      setExpandedFeatureIndex(expandedFeatureIndex - 1);
    }
  };

  // Update a feature property
  const updateFeature = (index, property, value) => {
    const newFeatures = [...features];
    newFeatures[index] = {
      ...newFeatures[index],
      [property]: value
    };
    updateContent('features', newFeatures);
  };

  // Move a feature up or down
  const moveFeature = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === features.length - 1)
    ) {
      return;
    }

    const newFeatures = [...features];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    [newFeatures[index], newFeatures[newIndex]] = [newFeatures[newIndex], newFeatures[index]];

    updateContent('features', newFeatures);

    // Update expanded index if needed
    if (expandedFeatureIndex === index) {
      setExpandedFeatureIndex(newIndex);
    } else if (expandedFeatureIndex === newIndex) {
      setExpandedFeatureIndex(index);
    }
  };

  const iconOptions = [
    'star', 'leaf', 'award', 'truck', 'shield', 'globe', 'clock', 'heart', 'plus', 'no icon'
  ];
  const FormSection = ({ title, children, description, icon: Icon }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center mb-4">
        {Icon && <Icon className="h-5 w-5 text-blue-600 mr-3" />}
        <div>
          <h4 className="font-semibold text-gray-900">{title}</h4>
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );
  
  const InputField = ({ label, value, onChange, placeholder, type = "text", rows, description }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
          rows={rows || 3}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder={placeholder}
        />
      )}
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );
  
  const ColorField = ({ label, color, onChange, description }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center space-x-3">
        <ColorPicker color={color} onChange={onChange} />
        <div 
          className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
          style={{ backgroundColor: color }}
        />
      </div>
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Features Section</h3>

<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-4">
    <Type className="h-5 w-5 text-blue-600 mr-3" />
    <div>
      <h4 className="font-semibold text-gray-900">Section Content</h4>
      <p className="text-sm text-gray-500 mt-1">Configure the main heading and description for your features section</p>
    </div>
  </div>
  
  <div className="space-y-6">
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Section Title</label>
      <input
        type="text"
        value={content?.title || 'Why Choose Bag & Box?'}
        onChange={(e) => updateContent('title', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        placeholder="Enter the main section title"
      />
      <p className="text-xs text-gray-500">The main heading that appears above your features</p>
    </div>

    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Section Subtitle</label>
      <textarea
        value={content?.subtitle || 'We combine quality, innovation, and sustainability to deliver the perfect packaging solution for your brand.'}
        onChange={(e) => updateContent('subtitle', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
        rows="2"
        placeholder="Enter a descriptive subtitle (optional)"
      />
      <p className="text-xs text-gray-500">Optional subtitle that provides more context about your features</p>
    </div>

    <div>
      <h5 className="font-medium text-gray-900 mb-4">Text Colors</h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Title Color</label>
          <div className="flex items-center space-x-3">
            <ColorPicker
              color={content?.titleColor || '#111827'}
              onChange={(color) => updateContent('titleColor', color)}
            />
            <div 
              className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
              style={{ backgroundColor: content?.titleColor || '#111827' }}
            />
          </div>
          <p className="text-xs text-gray-500">Color for the main section title</p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Subtitle Color</label>
          <div className="flex items-center space-x-3">
            <ColorPicker
              color={content?.subtitleColor || '#4b5563'}
              onChange={(color) => updateContent('subtitleColor', color)}
            />
            <div 
              className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
              style={{ backgroundColor: content?.subtitleColor || '#4b5563' }}
            />
          </div>
          <p className="text-xs text-gray-500">Color for the subtitle text</p>
        </div>
      </div>
    </div>
  </div>
</div>

<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-purple-100 p-2 rounded-lg mr-3">
      <List className="h-5 w-5 text-purple-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Feature Items</h4>
      <p className="text-sm text-gray-500 mt-1">Add and customize individual features for your section</p>
    </div>
  </div>

  <div className="space-y-4">
    {features.map((feature, index) => (
      <div
        key={index}
        className="border border-gray-200 rounded-xl bg-gray-50 overflow-hidden hover:shadow-sm transition-shadow duration-200"
      >
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
          onClick={() => setExpandedFeatureIndex(expandedFeatureIndex === index ? null : index)}
        >
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Star className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <span className="font-medium text-gray-900">{feature.title || `Feature ${index + 1}`}</span>
              <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                {feature.description || 'No description set'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                moveFeature(index, 'up');
              }}
              disabled={index === 0}
              title="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                moveFeature(index, 'down');
              }}
              disabled={index === features.length - 1}
              title="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            
            <button
              type="button"
              className="p-2 text-red-400 hover:text-red-600 hover:bg-white rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                removeFeature(index);
              }}
              title="Delete feature"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            
            <div className="p-2">
              {expandedFeatureIndex === index ?
                <ChevronUp className="h-4 w-4 text-gray-600" /> :
                <ChevronDown className="h-4 w-4 text-gray-600" />}
            </div>
          </div>
        </div>

        {expandedFeatureIndex === index && (
          <div className="bg-white border-t border-gray-200 p-6">
            <div className="space-y-6">
              {/* Basic Content */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Basic Content</h5>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Feature Title</label>
                    <input
                      type="text"
                      value={feature.title || ''}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter feature title"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Feature Description</label>
                    <textarea
                      value={feature.description || ''}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      rows="3"
                      placeholder="Describe this feature"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Icon</label>
                    <select
                      value={feature.icon || 'star'}
                      onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      {iconOptions.map((icon) => (
                        <option key={icon} value={icon}>
                          {icon.charAt(0).toUpperCase() + icon.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Icon Colors */}
              {feature.icon !== 'no icon' && (
                <div>
                  <h5 className="font-medium text-gray-900 mb-4">Icon Colors</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Icon Color</label>
                      <div className="flex items-center space-x-3">
                        <ColorPicker
                          color={feature.iconColor || content?.iconColor || '#3b82f6'}
                          onChange={(color) => updateFeature(index, 'iconColor', color)}
                        />
                        <div 
                          className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                          style={{ backgroundColor: feature.iconColor || content?.iconColor || '#3b82f6' }}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Icon Background</label>
                      <div className="flex items-center space-x-3">
                        <ColorPicker
                          color={feature.iconBackgroundColor || content?.iconBackgroundColor || '#e0f2fe'}
                          onChange={(color) => updateFeature(index, 'iconBackgroundColor', color)}
                        />
                        <div 
                          className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                          style={{ backgroundColor: feature.iconBackgroundColor || content?.iconBackgroundColor || '#e0f2fe' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Card Colors */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Card Colors</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Background Color</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={feature.backgroundColor || content?.cardBackgroundColor || '#ffffff'}
                        onChange={(color) => updateFeature(index, 'backgroundColor', color)}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: feature.backgroundColor || content?.cardBackgroundColor || '#ffffff' }}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Border Color</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={feature.borderColor || content?.borderColor || '#e5e7eb'}
                        onChange={(color) => updateFeature(index, 'borderColor', color)}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: feature.borderColor || content?.borderColor || '#e5e7eb' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Colors */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Text Colors</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Title Color</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={feature.titleColor || content?.featureTitleColor || '#1f2937'}
                        onChange={(color) => updateFeature(index, 'titleColor', color)}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: feature.titleColor || content?.featureTitleColor || '#1f2937' }}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Description Color</label>
                    <div className="flex items-center space-x-3">
                      <ColorPicker
                        color={feature.descriptionColor || content?.featureDescriptionColor || '#6b7280'}
                        onChange={(color) => updateFeature(index, 'descriptionColor', color)}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                        style={{ backgroundColor: feature.descriptionColor || content?.featureDescriptionColor || '#6b7280' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Settings */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Border Settings</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Border Width</label>
                    <input
                      type="text"
                      value={feature.borderWidth || '1px'}
                      onChange={(e) => updateFeature(index, 'borderWidth', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., 1px, 2px"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Border Radius</label>
                    <input
                      type="text"
                      value={feature.borderRadius || content?.cardBorderRadius || '0.75rem'}
                      onChange={(e) => updateFeature(index, 'borderRadius', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., 0.5rem, 8px"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Border Style</label>
                    <select
                      value={feature.borderStyle || 'solid'}
                      onChange={(e) => updateFeature(index, 'borderStyle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="solid">Solid</option>
                      <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                      <option value="double">Double</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Spacing */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Spacing</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Padding</label>
                    <input
                      type="text"
                      value={feature.padding || '1.5rem'}
                      onChange={(e) => updateFeature(index, 'padding', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., 1.5rem, 24px"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Margin</label>
                    <input
                      type="text"
                      value={feature.margin || '0'}
                      onChange={(e) => updateFeature(index, 'margin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., 1rem, 16px"
                    />
                  </div>
                </div>
              </div>

              {/* Shadow Settings */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Shadow Settings</h5>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        id={`feature-shadow-${index}`}
                        checked={feature.boxShadow !== false}
                        onChange={(e) => updateFeature(index, 'boxShadow', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor={`feature-shadow-${index}`} className="text-sm font-medium text-gray-700">Enable Box Shadow</label>
                      <p className="text-xs text-gray-500 mt-1">Add a drop shadow effect to the feature card</p>
                    </div>
                  </div>

                  {feature.boxShadow !== false && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Custom Shadow</label>
                      <input
                        type="text"
                        value={feature.customShadow || '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
                        onChange={(e) => updateFeature(index, 'customShadow', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="e.g., 0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Layout & Typography */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Layout & Typography</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Text Alignment</label>
                    <select
                      value={feature.textAlign || 'center'}
                      onChange={(e) => updateFeature(index, 'textAlign', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Icon Size</label>
                    <select
                      value={feature.iconSize || content?.iconSize || 'large'}
                      onChange={(e) => updateFeature(index, 'iconSize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                      <option value="extra-large">Extra Large</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Title Font Size</label>
                    <input
                      type="text"
                      value={feature.titleFontSize || '1.25rem'}
                      onChange={(e) => updateFeature(index, 'titleFontSize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., 1.25rem, 20px"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Description Font Size</label>
                    <input
                      type="text"
                      value={feature.descriptionFontSize || '1rem'}
                      onChange={(e) => updateFeature(index, 'descriptionFontSize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., 1rem, 16px"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Title Font Weight</label>
                    <select
                      value={feature.titleFontWeight || 'medium'}
                      onChange={(e) => updateFeature(index, 'titleFontWeight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="normal">Normal</option>
                      <option value="medium">Medium</option>
                      <option value="semibold">Semi Bold</option>
                      <option value="bold">Bold</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Description Font Weight</label>
                    <select
                      value={feature.descriptionFontWeight || 'normal'}
                      onChange={(e) => updateFeature(index, 'descriptionFontWeight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="light">Light</option>
                      <option value="normal">Normal</option>
                      <option value="medium">Medium</option>
                      <option value="semibold">Semi Bold</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Hover Effects */}
              <div>
                <h5 className="font-medium text-gray-900 mb-4">Hover Effects</h5>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        id={`feature-hover-${index}`}
                        checked={feature.hoverEffect !== false}
                        onChange={(e) => updateFeature(index, 'hoverEffect', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor={`feature-hover-${index}`} className="text-sm font-medium text-gray-700">Enable Hover Effect</label>
                      <p className="text-xs text-gray-500 mt-1">Add interactive hover animations to the feature card</p>
                    </div>
                  </div>

                  {feature.hoverEffect !== false && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Hover Background Color</label>
                        <div className="flex items-center space-x-3">
                          <ColorPicker
                            color={feature.hoverBackgroundColor || '#f3f4f6'}
                            onChange={(color) => updateFeature(index, 'hoverBackgroundColor', color)}
                          />
                          <div 
                            className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                            style={{ backgroundColor: feature.hoverBackgroundColor || '#f3f4f6' }}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Hover Transform</label>
                        <select
                          value={feature.hoverTransform || 'scale'}
                          onChange={(e) => updateFeature(index, 'hoverTransform', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          <option value="none">None</option>
                          <option value="scale">Scale Up</option>
                          <option value="translateY">Move Up</option>
                          <option value="rotate">Rotate</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>

  <button
    type="button"
    className="mt-6 flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
    onClick={addFeature}
  >
    <Plus className="h-5 w-5 mr-2" />
    Add New Feature
  </button>
</div>

<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-indigo-100 p-2 rounded-lg mr-3">
      <Palette className="h-5 w-5 text-indigo-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Section Styling</h4>
      <p className="text-sm text-gray-500 mt-1">Customize the overall appearance and layout of your features section</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Background Color */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Background</h5>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Section Background Color</label>
        <div className="flex items-center space-x-3">
          <ColorPicker
            color={content?.backgroundColor || '#ffffff'}
            onChange={(color) => updateContent('backgroundColor', color)}
          />
          
        </div>
        <p className="text-xs text-gray-500">Background color for the entire features section</p>
      </div>
    </div>

    {/* Grid Layout */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Grid Layout</h5>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">Configure how many feature cards appear per row on different screen sizes</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Mobile (phones)</label>
            <select
              value={content?.columnsPerRow?.mobile || 1}
              onChange={(e) => updateContent('columnsPerRow', {
                ...content?.columnsPerRow,
                mobile: parseInt(e.target.value)
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Tablet</label>
            <select
              value={content?.columnsPerRow?.tablet || 2}
              onChange={(e) => updateContent('columnsPerRow', {
                ...content?.columnsPerRow,
                tablet: parseInt(e.target.value)
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Desktop</label>
            <select
              value={content?.columnsPerRow?.desktop || 4}
              onChange={(e) => updateContent('columnsPerRow', {
                ...content?.columnsPerRow,
                desktop: parseInt(e.target.value)
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
              <option value={5}>5 Columns</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    {/* Advanced Settings Toggle */}
    <div className="border-t border-gray-200 pt-6">
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
        onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
      >
        <Settings className="h-4 w-4 mr-2" />
        {showAdvancedSettings ? 'Hide Advanced Options' : 'Show Advanced Options'}
        {showAdvancedSettings ? (
          <ChevronUp className="h-4 w-4 ml-2" />
        ) : (
          <ChevronDown className="h-4 w-4 ml-2" />
        )}
      </button>
    </div>

    {/* Advanced Settings */}
    {showAdvancedSettings && (
      <div className="space-y-6 pt-4 border-t border-gray-200">
        {/* Default Text Colors */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Default Text Colors</h5>
          <p className="text-sm text-gray-600 mb-4">These colors apply to all features unless overridden individually</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Feature Title Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.featureTitleColor || '#1f2937'}
                  onChange={(color) => updateContent('featureTitleColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.featureTitleColor || '#1f2937' }}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Feature Description Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.featureDescriptionColor || '#6b7280'}
                  onChange={(color) => updateContent('featureDescriptionColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.featureDescriptionColor || '#6b7280' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Default Icon Colors */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Default Icon Colors</h5>
          <p className="text-sm text-gray-600 mb-4">Default icon styling for all features</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Icon Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.iconColor || '#3b82f6'}
                  onChange={(color) => updateContent('iconColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.iconColor || '#3b82f6' }}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Icon Background Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.iconBackgroundColor || '#e0f2fe'}
                  onChange={(color) => updateContent('iconBackgroundColor', color)}
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: content?.iconBackgroundColor || '#e0f2fe' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card Styling */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Card Styling</h5>
          <div className="space-y-6">
            {/* Card Background */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Card Background Color</label>
              <div className="flex items-center space-x-3">
                <ColorPicker
                  color={content?.cardBackgroundColor || '#ffffff'}
                  onChange={(color) => updateContent('cardBackgroundColor', color)}
                />
                
              </div>
              <p className="text-xs text-gray-500">Background color for individual feature cards</p>
            </div>

            {/* Border Settings */}
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="card-border"
                    checked={content?.borderEnabled !== false}
                    onChange={(e) => updateContent('borderEnabled', e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="card-border" className="text-sm font-medium text-gray-700">Show Card Borders</label>
                  <p className="text-xs text-gray-500 mt-1">Add borders around each feature card</p>
                </div>
              </div>

              {content?.borderEnabled !== false && (
                <div className="space-y-2 pl-7">
                  <label className="block text-sm font-medium text-gray-700">Border Color</label>
                  <div className="flex items-center space-x-3">
                    <ColorPicker
                      color={content?.borderColor || '#e5e7eb'}
                      onChange={(color) => updateContent('borderColor', color)}
                    />
                    
                  </div>
                </div>
              )}
            </div>

            {/* Shadow Settings */}
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="card-shadow"
                  checked={content?.cardShadow !== false}
                  onChange={(e) => updateContent('cardShadow', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="card-shadow" className="text-sm font-medium text-gray-700">Show Card Shadows</label>
                <p className="text-xs text-gray-500 mt-1">Add drop shadows to feature cards for depth</p>
              </div>
            </div>

            {/* Border Radius */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Card Border Radius</label>
              <input
                type="text"
                value={content?.cardBorderRadius || '0.75rem'}
                onChange={(e) => updateContent('cardBorderRadius', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="e.g., 0.5rem, 8px, 0"
              />
              <p className="text-xs text-gray-500">Controls how rounded the card corners are</p>
            </div>
          </div>
        </div>

        {/* Layout Options */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Layout Options</h5>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="align-center"
                  checked={content?.alignCenter !== false}
                  onChange={(e) => updateContent('alignCenter', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="align-center" className="text-sm font-medium text-gray-700">Center Align Feature Content</label>
                <p className="text-xs text-gray-500 mt-1">Center all text and icons within each feature card</p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Default Icon Size</label>
              <select
                value={content?.iconSize || 'large'}
                onChange={(e) => updateContent('iconSize', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
              <p className="text-xs text-gray-500">Default size for all feature icons</p>
            </div>
          </div>
        </div>

        {/* Title Divider */}
        <div>
          <h5 className="font-medium text-gray-900 mb-4">Title Divider</h5>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="show-divider"
                  checked={content?.showDivider !== false}
                  onChange={(e) => updateContent('showDivider', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="show-divider" className="text-sm font-medium text-gray-700">Show Divider Below Title</label>
                <p className="text-xs text-gray-500 mt-1">Add a decorative line below the section title</p>
              </div>
            </div>

            {content?.showDivider !== false && (
              <div className="space-y-2 pl-7">
                <label className="block text-sm font-medium text-gray-700">Divider Color</label>
                <div className="flex items-center space-x-3">
                  <ColorPicker
                    color={content?.dividerColor || '#e5e7eb'}
                    onChange={(color) => updateContent('dividerColor', color)}
                  />
                  
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
</div>
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
  <div className="flex items-center mb-6">
    <div className="bg-green-100 p-2 rounded-lg mr-3">
      <Space className="h-5 w-5 text-green-600" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">Spacing & Animation</h4>
      <p className="text-sm text-gray-500 mt-1">Control the space around the section and add smooth animations</p>
    </div>
  </div>

  <div className="space-y-6">
    {/* Spacing Settings */}
    <div>
      <h5 className="font-medium text-gray-900 mb-4">Section Spacing</h5>
      <p className="text-sm text-gray-600 mb-4">Adjust the space around the entire features section</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Top Padding</label>
          <input
            type="text"
            value={content?.paddingTop || '4rem'}
            onChange={(e) => updateContent('paddingTop', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="e.g., 4rem, 64px"
          />
          <p className="text-xs text-gray-500">Space above the section</p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Bottom Padding</label>
          <input
            type="text"
            value={content?.paddingBottom || '4rem'}
            onChange={(e) => updateContent('paddingBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="e.g., 4rem, 64px"
          />
          <p className="text-xs text-gray-500">Space below the section</p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Bottom Margin</label>
          <input
            type="text"
            value={content?.marginBottom || '2rem'}
            onChange={(e) => updateContent('marginBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="e.g., 2rem, 32px"
          />
          <p className="text-xs text-gray-500">External space after section</p>
        </div>
      </div>
    </div>

    {/* Animation Settings */}
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center mb-4">
        <div className="bg-purple-100 p-2 rounded-lg mr-3">
          <Play className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h5 className="font-medium text-gray-900">Animation Settings</h5>
          <p className="text-sm text-gray-500">Add smooth entrance animations to feature cards</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              id="animation-enabled"
              checked={content?.animationEnabled !== false}
              onChange={(e) => updateContent('animationEnabled', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="animation-enabled" className="text-sm font-medium text-gray-700">Enable Animations</label>
            <p className="text-xs text-gray-500 mt-1">Add fade-in animations when feature cards come into view</p>
          </div>
        </div>

        {content?.animationEnabled !== false && (
          <div className="pl-7 space-y-2">
            <label className="block text-sm font-medium text-gray-700">Animation Delay</label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                value={content?.animationDelay || 100}
                onChange={(e) => updateContent('animationDelay', parseInt(e.target.value))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                min="0"
                max="1000"
                step="50"
              />
              <span className="text-sm text-gray-500">milliseconds</span>
            </div>
            <p className="text-xs text-gray-500">
              Delay between each feature card animation. Higher values create a more staggered effect.
            </p>
            
            {/* Animation Preview Helper
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                <span className="text-xs font-medium text-blue-800">Animation Preview</span>
              </div>
              <p className="text-xs text-blue-700">
                {content?.animationDelay <= 50 && "Very fast staggered animation"}
                {content?.animationDelay > 50 && content?.animationDelay <= 150 && "Quick staggered animation"}
                {content?.animationDelay > 150 && content?.animationDelay <= 300 && "Moderate staggered animation"}
                {content?.animationDelay > 300 && "Slow staggered animation"}
              </p>
            </div> */}
          </div>
        )}
      </div>
    </div>
  </div>
</div>
    </div>
  );
};

export default FeaturesForm;